// ============================================================
// AI PARADIGM TYPES - Layer 6, Sub-Layer 6.7
// Complete taxonomy of AI paradigms with implementation patterns
// ============================================================

export const AI_PARADIGM_TYPES = {
  
  layerId: 'layer6',
  subLayerId: 'sl6.7',
  subLayerName: 'AI Paradigm Types',
  meaning: 'The paradigm of intelligence defining how AI thinks, behaves, and interacts',
  
  paradigms: [
    
    // ==================== GENERATIVE AI ====================
    {
      id: 'paradigm-generative',
      name: 'Generative AI',
      icon: '🎨',
      category: 'generative',
      purpose: 'Create new content (text, images, audio, video, code, 3D)',
      function: 'Learn data distribution and generate novel samples from it',
      
      description: 'AI systems that generate new content across modalities',
      
      subTypes: [
        'Text Generation (LLMs)',
        'Image Generation (Diffusion, GAN)',
        'Video Generation (Sora, Runway)',
        'Audio/Music Generation (Suno, ElevenLabs)',
        'Code Generation (Copilot, Cursor)',
        '3D/Scene Generation (NeRF, Gaussian Splatting)',
        'Multi-Modal Generation (GPT-4o, Gemini)'
      ],
      
      essentialComponents: [
        'Transformer', 'Diffusion Model', 'GAN', 'VAE',
        'GPU (NVIDIA)', 'HBM (High Bandwidth Memory)',
        'Tokenization (BPE)', 'Embedding Generation',
        'Cross-Entropy', 'Adam', 'KV Cache',
        'Prompt Templates', 'Guardrails', 'Safety Classifiers'
      ],
      
      recommendedComponents: [
        'RLHF Pipeline', 'DPO', 'Constitutional AI',
        'Liquid Cooling', 'InfiniBand',
        'Semantic Caching', 'Model Distillation'
      ],
      
      infrastructure: {
        gpuTier: 'High-end (A100/H100)',
        memory: '80GB+ HBM',
        storage: 'NVMe SSD',
        network: 'InfiniBand (for distributed training)',
        cooling: 'Liquid cooling recommended'
      },
      
      costProfile: {
        training: { low: 10000, high: 100000000, unit: 'per model' },
        inference: { low: 0.001, high: 0.10, unit: 'per 1K tokens' },
        hosting: { low: 500, high: 100000, unit: 'per month' }
      },
      
      useCases: [
        'Content creation (marketing, media)',
        'Code assistance',
        'Customer engagement',
        'Product design',
        'Research assistance',
        'Personalization'
      ],
      
      maturityLevel: 'Production Ready',
      adoptionRate: 'Very High (2024+)',
      
      alternatives: [
        { name: 'Discriminative AI', when: 'If only classification needed' },
        { name: 'Retrieval-based', when: 'If content exists, just retrieve' }
      ]
    },
    
    // ==================== AGENTIC AI ====================
    {
      id: 'paradigm-agentic',
      name: 'Agentic AI',
      icon: '🤖',
      category: 'agentic',
      purpose: 'Autonomous goal-directed behavior with planning and tool use',
      function: 'Perceive → Plan → Act → Learn loops with memory and reasoning',
      
      description: 'AI systems that autonomously plan, reason, and execute complex multi-step tasks',
      
      subTypes: [
        'Reactive Agents (Reflex-based)',
        'Deliberative Agents (Planning)',
        'Hybrid Agents (BDI - Belief-Desire-Intention)',
        'Learning Agents (RL-based)',
        'Multi-Agent Systems (Swarm)',
        'Hierarchical Agents (Manager-Worker)',
        'Tool-Using Agents (Function Calling)',
        'Autonomous Agents (Full autonomy)'
      ],
      
      essentialComponents: [
        'Transformer', 'Agentic Tool Usage', 'Prompt Templates',
        'RAG Pipelines', 'Memory Systems', 'Planning Engine',
        'ReAct (Reasoning + Acting)', 'Tool Calling APIs',
        'Guardrails', 'Kill Switch', 'Observability'
      ],
      
      recommendedComponents: [
        'Multi-Agent Orchestration', 'Mixture of Experts (MoE)',
        'Human-in-the-loop', 'Escalation Path',
        'Session Manager', 'Feature Flags',
        'Task Queues', 'State Machine'
      ],
      
      agenticPatterns: [
        {
          id: 'react',
          name: 'ReAct (Reasoning + Acting)',
          description: 'Interleave reasoning traces with actions',
          bestFor: 'General task completion'
        },
        {
          id: 'plan-execute',
          name: 'Plan-and-Execute',
          description: 'Plan entire task, then execute steps',
          bestFor: 'Complex multi-step tasks'
        },
        {
          id: 'reflexion',
          name: 'Reflexion',
          description: 'Self-reflection and iterative improvement',
          bestFor: 'Quality-critical tasks'
        },
        {
          id: 'tree-of-thoughts',
          name: 'Tree of Thoughts',
          description: 'Explore multiple reasoning paths',
          bestFor: 'Puzzle solving, math'
        },
        {
          id: 'self-consistency',
          name: 'Self-Consistency',
          description: 'Sample multiple responses, vote on best',
          bestFor: 'Reasoning tasks'
        },
        {
          id: 'manager-worker',
          name: 'Manager-Worker',
          description: 'Hierarchical delegation',
          bestFor: 'Complex enterprise workflows'
        }
      ],
      
      agenticTools: [
        'Code Interpreter', 'Web Search', 'File Operations',
        'Database Queries', 'API Calls', 'Email/Calendar',
        'Calculator', 'Browser Automation', 'Custom Functions'
      ],
      
      memoryTypes: [
        'Short-term (Context Window)',
        'Long-term (Vector DB)',
        'Episodic (Session History)',
        'Semantic (Knowledge Graph)',
        'Procedural (Skill Library)',
        'Working (Scratchpad)'
      ],
      
      autonomyLevels: [
        { level: 1, name: 'Assisted', description: 'Human does all actions, AI suggests' },
        { level: 2, name: 'Partial', description: 'Human approves each AI action' },
        { level: 3, name: 'Conditional', description: 'AI acts within defined boundaries' },
        { level: 4, name: 'High', description: 'AI acts with human oversight' },
        { level: 5, name: 'Full', description: 'Fully autonomous with kill switch' }
      ],
      
      infrastructure: {
        gpuTier: 'High-end (A100/H100)',
        memory: '64GB+ HBM',
        orchestration: 'Kubernetes + Argo',
        latency: 'Standard to real-time'
      },
      
      costProfile: {
        setup: { low: 5000, high: 100000, unit: 'per project' },
        perTask: { low: 0.01, high: 10.00, unit: 'per task' },
        hosting: { low: 1000, high: 50000, unit: 'per month' }
      },
      
      useCases: [
        'Software development agents',
        'Research automation',
        'Customer service automation',
        'Business process automation',
        'DevOps/SRE automation',
        'Personal assistants',
        'Robotic process automation (RPA)'
      ],
      
      maturityLevel: 'Emerging (2024)',
      adoptionRate: 'Growing rapidly',
      
      risks: [
        'Unintended actions',
        'Runaway execution costs',
        'Security vulnerabilities',
        'Prompt injection',
        'Hallucination amplification',
        'Loss of control'
      ],
      
      mitigations: [
        'Kill switch',
        'Human-in-the-loop',
        'Sandboxing',
        'Budget limits',
        'Action whitelisting',
        'Audit logging',
        'Rate limiting'
      ],
      
      alternatives: [
        { name: 'Conversational AI', when: 'Only Q&A needed, no autonomy' },
        { name: 'Workflow Automation', when: 'Steps are fixed and known' }
      ]
    },
    
    // ==================== DISCRIMINATIVE AI ====================
    {
      id: 'paradigm-discriminative',
      name: 'Discriminative AI',
      icon: '🎯',
      category: 'discriminative',
      purpose: 'Classify, categorize, or distinguish between data points',
      function: 'Learn decision boundaries between classes',
      
      description: 'AI systems that learn to distinguish between categories or predict labels',
      
      subTypes: [
        'Binary Classification',
        'Multi-class Classification',
        'Multi-label Classification',
        'Anomaly Detection',
        'Named Entity Recognition',
        'Object Detection'
      ],
      
      essentialComponents: [
        'XGBoost', 'Random Forest', 'Logistic Regression',
        'SVM', 'Cross-Entropy', 'One-Hot Encoding',
        'Normalization', 'SHAP'
      ],
      
      recommendedComponents: [
        'LIME', 'Feature Selection (PCA)', 'SMOTE',
        'Great Expectations', 'Evidently AI'
      ],
      
      infrastructure: {
        gpuTier: 'Optional (CPU sufficient)',
        memory: '16-64 GB',
        storage: 'SSD SATA',
        deployment: 'Standard'
      },
      
      costProfile: {
        training: { low: 0, high: 5000, unit: 'per model' },
        inference: { low: 0.0001, high: 0.01, unit: 'per prediction' }
      },
      
      useCases: [
        'Fraud detection',
        'Spam filtering',
        'Sentiment analysis',
        'Medical diagnosis',
        'Quality control',
        'Risk assessment'
      ],
      
      maturityLevel: 'Mature',
      adoptionRate: 'Very High',
      
      alternatives: [
        { name: 'Generative AI', when: 'Need to create content' },
        { name: 'Predictive AI', when: 'Predict continuous values' }
      ]
    },
    
    // ==================== PREDICTIVE AI ====================
    {
      id: 'paradigm-predictive',
      name: 'Predictive AI',
      icon: '📈',
      category: 'predictive',
      purpose: 'Forecast future events or values',
      function: 'Learn patterns and extrapolate',
      
      description: 'AI systems that predict future outcomes based on historical data',
      
      subTypes: [
        'Time Series Forecasting',
        'Regression',
        'Demand Prediction',
        'Failure Prediction',
        'Churn Prediction',
        'Trend Analysis'
      ],
      
      essentialComponents: [
        'LSTM', 'XGBoost', 'Linear Regression',
        'Prophet', 'ARIMA', 'Time Series Analysis',
        'MSE', 'Adam'
      ],
      
      infrastructure: {
        gpuTier: 'Optional',
        memory: '32-128 GB',
        storage: 'Time-series DB (InfluxDB)'
      },
      
      costProfile: {
        training: { low: 0, high: 10000, unit: 'per model' },
        inference: { low: 0.0001, high: 0.01, unit: 'per prediction' }
      },
      
      useCases: [
        'Sales forecasting',
        'Demand planning',
        'Predictive maintenance',
        'Risk prediction',
        'Churn prevention'
      ],
      
      maturityLevel: 'Mature',
      adoptionRate: 'Very High'
    },
    
    // ==================== PRESCRIPTIVE AI ====================
    {
      id: 'paradigm-prescriptive',
      name: 'Prescriptive AI',
      icon: '💊',
      category: 'prescriptive',
      purpose: 'Recommend actions to achieve desired outcomes',
      function: 'Optimize decisions given constraints',
      
      description: 'AI systems that recommend the best action to take',
      
      subTypes: [
        'Optimization',
        'Recommendation Systems',
        'Decision Support',
        'Resource Allocation',
        'Portfolio Optimization'
      ],
      
      essentialComponents: [
        'Genetic Algorithm', 'Simulated Annealing',
        'Linear Programming', 'Reinforcement Learning',
        'Causal AI', 'Monte Carlo Tree Search'
      ],
      
      infrastructure: {
        gpuTier: 'Optional',
        memory: '32-128 GB',
        storage: 'Standard'
      },
      
      costProfile: {
        setup: { low: 1000, high: 50000, unit: 'per project' },
        inference: { low: 0.001, high: 0.10, unit: 'per recommendation' }
      },
      
      useCases: [
        'Dynamic pricing',
        'Resource scheduling',
        'Supply chain optimization',
        'Investment advice',
        'Treatment planning'
      ],
      
      maturityLevel: 'Growing',
      adoptionRate: 'High'
    },
    
    // ==================== CONVERSATIONAL AI ====================
    {
      id: 'paradigm-conversational',
      name: 'Conversational AI',
      icon: '💬',
      category: 'conversational',
      purpose: 'Engage in natural language dialogue',
      function: 'Understand intent, maintain context, generate responses',
      
      description: 'AI systems designed for human-like conversation',
      
      subTypes: [
        'Chatbots',
        'Voice Assistants',
        'Virtual Agents',
        'Copilots',
        'Multi-turn Dialogue Systems'
      ],
      
      essentialComponents: [
        'Transformer', 'Tokenization (BPE)', 'Chatbot',
        'RLHF Pipeline', 'Guardrails', 'Safety Classifiers',
        'Prompt Templates', 'Embedding Generation'
      ],
      
      infrastructure: {
        gpuTier: 'High-end (A100/H100)',
        memory: '80GB+ HBM',
        latency: 'Real-time (< 500ms)',
        availability: '99.9%+'
      },
      
      costProfile: {
        perConversation: { low: 0.01, high: 1.00, unit: 'per conversation' },
        hosting: { low: 500, high: 20000, unit: 'per month' }
      },
      
      useCases: [
        'Customer support',
        'Sales assistance',
        'Technical help desk',
        'Personal assistants',
        'Educational tutoring'
      ],
      
      maturityLevel: 'Production Ready',
      adoptionRate: 'Very High'
    },
    
    // ==================== MULTIMODAL AI ====================
    {
      id: 'paradigm-multimodal',
      name: 'Multimodal AI',
      icon: '👁️',
      category: 'multimodal',
      purpose: 'Process and generate across multiple modalities',
      function: 'Jointly understand text, image, audio, video, and 3D',
      
      description: 'AI systems that work across multiple data types simultaneously',
      
      subTypes: [
        'Vision-Language Models (VLM)',
        'Audio-Visual Models',
        'Text-Image Generation',
        'Video Understanding',
        'Sensor Fusion'
      ],
      
      essentialComponents: [
        'Transformer', 'CNN', 'Embedding Generation',
        'Cross-Attention', 'Modality Fusion',
        'GPU (NVIDIA)', 'HBM (High Bandwidth Memory)'
      ],
      
      infrastructure: {
        gpuTier: 'High-end (H100 recommended)',
        memory: '160GB+ (2x H100)',
        storage: 'NVMe SSD',
        network: 'InfiniBand'
      },
      
      costProfile: {
        training: { low: 100000, high: 10000000, unit: 'per model' },
        inference: { low: 0.01, high: 1.00, unit: 'per inference' }
      },
      
      useCases: [
        'Image captioning',
        'Visual question answering',
        'Content moderation',
        'Medical imaging + reports',
        'Autonomous driving'
      ],
      
      maturityLevel: 'Production Ready (2024)',
      adoptionRate: 'Very High'
    },
    
    // ==================== AUTONOMOUS AI ====================
    {
      id: 'paradigm-autonomous',
      name: 'Autonomous AI',
      icon: '🚗',
      category: 'autonomous',
      purpose: 'Operate independently in physical or digital environments',
      function: 'Sense → Plan → Act without human intervention',
      
      description: 'AI systems that operate autonomously in real-world scenarios',
      
      subTypes: [
        'Self-Driving Vehicles',
        'Drones',
        'Robotics',
        'Industrial Automation',
        'Trading Systems'
      ],
      
      essentialComponents: [
        'CNN', 'LSTM', 'Reinforcement Learning',
        'Sensor Fusion', 'Real-time Processing',
        'Safety Classifiers', 'Kill Switch',
        'FPGA', 'Edge AI', 'On-device Execution'
      ],
      
      infrastructure: {
        gpuTier: 'Edge-optimized (Jetson, custom silicon)',
        memory: '16-64 GB',
        latency: 'Ultra-low (< 10ms)',
        power: 'Battery-constrained',
        safety: 'ASIL-D (automotive)'
      },
      
      costProfile: {
        perSystem: { low: 1000, high: 100000, unit: 'per unit' },
        maintenance: { low: 100, high: 10000, unit: 'per month' }
      },
      
      useCases: [
        'Autonomous vehicles',
        'Delivery robots',
        'Industrial automation',
        'Algorithmic trading',
        'Aerospace systems'
      ],
      
      maturityLevel: 'Emerging',
      adoptionRate: 'Growing (specific verticals)',
      
      risks: [
        'Safety incidents',
        'Regulatory hurdles',
        'Edge cases',
        'Liability questions'
      ]
    },
    
    // ==================== COLLABORATIVE AI ====================
    {
      id: 'paradigm-collaborative',
      name: 'Collaborative AI',
      icon: '👥',
      category: 'collaborative',
      purpose: 'Work alongside humans as a team member',
      function: 'Augment human capabilities, not replace',
      
      description: 'AI systems designed to collaborate with humans',
      
      subTypes: [
        'Human-in-the-loop',
        'Human-on-the-loop',
        'Copilots',
        'Assistants',
        'Team Agents'
      ],
      
      essentialComponents: [
        'Transformer', 'Human-in-the-loop', 'Copilot',
        'Review Queue', 'Escalation Path',
        'Explainability (SHAP, LIME)'
      ],
      
      costProfile: {
        perUser: { low: 10, high: 100, unit: 'per user/month' },
        setup: { low: 5000, high: 50000, unit: 'per project' }
      },
      
      useCases: [
        'Code copilots',
        'Design assistants',
        'Research partners',
        'Writing assistants',
        'Medical decision support'
      ],
      
      maturityLevel: 'Production Ready',
      adoptionRate: 'Very High'
    },
    
    // ==================== EXPLAINABLE AI ====================
    {
      id: 'paradigm-explainable',
      name: 'Explainable AI (XAI)',
      icon: '🔍',
      category: 'explainable',
      purpose: 'Provide interpretable reasoning for decisions',
      function: 'Make AI decisions transparent and auditable',
      
      description: 'AI systems that explain their decisions in human-understandable terms',
      
      subTypes: [
        'Post-hoc Explanations',
        'Intrinsic Interpretability',
        'Counterfactual Explanations',
        'Attention-based Explanations'
      ],
      
      essentialComponents: [
        'SHAP', 'LIME', 'Integrated Gradients',
        'Attention Visualization', 'Model Cards',
        'Decision Tree', 'Explainable AI'
      ],
      
      useCases: [
        'Healthcare diagnosis',
        'Financial lending',
        'Criminal justice',
        'Hiring decisions',
        'Regulatory compliance'
      ],
      
      maturityLevel: 'Production Ready',
      adoptionRate: 'High (regulated industries)',
      
      regulatoryDrivers: [
        'EU AI Act (right to explanation)',
        'GDPR Article 22',
        'Fair Lending Laws',
        'FDA guidance for SaMD'
      ]
    },
    
    // ==================== ADDITIONAL PARADIGMS ====================
    
    {
      id: 'paradigm-causal',
      name: 'Causal AI',
      icon: '🔗',
      category: 'causal',
      purpose: 'Understand cause-effect relationships',
      function: 'Move beyond correlation to causation',
      
      description: 'AI systems that infer causal relationships from data',
      
      essentialComponents: [
        'Causal AI', 'Bayesian Networks',
        'Structural Equation Models',
        'Monte Carlo Simulation'
      ],
      
      useCases: [
        'Policy evaluation',
        'Marketing attribution',
        'Medical treatment effect',
        'Root cause analysis'
      ]
    },
    
    {
      id: 'paradigm-federated',
      name: 'Federated AI',
      icon: '🔒',
      category: 'federated',
      purpose: 'Train across decentralized data sources',
      function: 'Privacy-preserving distributed learning',
      
      description: 'AI systems that train without centralizing data',
      
      essentialComponents: [
        'Federated Learning', 'Differential Privacy',
        'Secure Aggregation', 'ZK-AI'
      ],
      
      useCases: [
        'Healthcare (hospital collaboration)',
        'Banking (fraud detection)',
        'Mobile keyboards',
        'IoT networks'
      ]
    },
    
    {
      id: 'paradigm-neuro-symbolic',
      name: 'Neuro-Symbolic AI',
      icon: '🧠',
      category: 'neuro-symbolic',
      purpose: 'Combine neural networks with symbolic reasoning',
      function: 'Get best of both: learning + logic',
      
      description: 'AI systems combining deep learning with symbolic AI',
      
      essentialComponents: [
        'Neuro-Symbolic AI', 'Knowledge Graphs',
        'Rule Engine', 'Ontology'
      ],
      
      useCases: [
        'Legal reasoning',
        'Scientific discovery',
        'Complex planning',
        'Explainable decisions'
      ]
    }
  ]
};

// ============================================================
// PARADIGM SELECTION LOGIC
// ============================================================

export function getRecommendedParadigm(requirements) {
  const { problemTypes = [], dataTypes = [], industry, priority, latency } = requirements;
  
  const scores = {};
  
  // Initialize all paradigms with base score
  AI_PARADIGM_TYPES.paradigms.forEach(p => {
    scores[p.id] = 0;
  });
  
  // Score based on problem types
  problemTypes.forEach(pt => {
    switch(pt) {
      case 'generation':
        scores['paradigm-generative'] += 30;
        break;
      case 'chatbot':
        scores['paradigm-conversational'] += 30;
        scores['paradigm-generative'] += 15;
        break;
      case 'classification':
      case 'detection':
        scores['paradigm-discriminative'] += 25;
        break;
      case 'forecasting':
      case 'regression':
        scores['paradigm-predictive'] += 25;
        break;
      case 'recommendation':
        scores['paradigm-prescriptive'] += 20;
        scores['paradigm-collaborative'] += 10;
        break;
      case 'optimization':
        scores['paradigm-prescriptive'] += 25;
        break;
      case 'automation':
        scores['paradigm-agentic'] += 25;
        break;
    }
  });
  
  // Score based on data types
  dataTypes.forEach(dt => {
    if (['image', 'video', 'audio'].includes(dt)) {
      scores['paradigm-multimodal'] += 20;
    }
  });
  
  // Score based on industry
  if (['healthcare', 'finance', 'legal'].includes(industry)) {
    scores['paradigm-explainable'] += 20;
    scores['paradigm-collaborative'] += 10;
  }
  
  if (['autonomous', 'automotive'].includes(industry)) {
    scores['paradigm-autonomous'] += 30;
  }
  
  // Score based on priority
  if (priority === 'quality') {
    scores['paradigm-explainable'] += 15;
    scores['paradigm-collaborative'] += 15;
  }
  
  if (priority === 'speed') {
    scores['paradigm-discriminative'] += 10;
  }
  
  // Score based on latency
  if (latency === 'ultra-low') {
    scores['paradigm-autonomous'] += 20;
    scores['paradigm-discriminative'] += 10;
  }
  
  // Get top 3 paradigms
  return Object.entries(scores)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([id, score]) => ({
      paradigm: AI_PARADIGM_TYPES.paradigms.find(p => p.id === id),
      score: Math.min(score, 100)
    }));
}

export function getParadigmByName(name) {
  return AI_PARADIGM_TYPES.paradigms.find(p => p.name === name);
}

export function getAllParadigms() {
  return AI_PARADIGM_TYPES.paradigms;
}

export default AI_PARADIGM_TYPES;